import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type {
	APIData,
	CreateRecordProperties,
	RecordData,
	RequestError,
	Service,
	TemplateResolve,
} from 'webapp-types';
import { BillingFeature, ModelName, PopupEvent, TemplateObjectType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy, sortByPopularity } from 'webapp/utils/common';

import TemplatesAccountPopupDeleteConfirmComponent from 'webapp/components/templates/account/popup-delete-confirm';
import TemplatesAccountPopupEditComponent from 'webapp/components/templates/account/popup-edit';
import TemplatesPopupResolveTemplateComponent from 'webapp/components/templates/popup-resolve-template';
import TemplatesPublicPopupEditComponent from 'webapp/components/templates/public/popup-edit';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type Report from 'webapp/models-custom/report';
import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';
import type { TemplateMetricKey } from 'webapp/models-custom/template';
import Template from 'webapp/models-custom/template';
import type TemplateRecord from 'webapp/models/template';

import type { TrackingOriginForTemplateCreated as TrackingOriginForBoardLoopAccountTemplateCreated } from 'webapp/services/model-manager/board-loops';
import type { TrackingOriginForTemplateCreated as TrackingOriginForBoardAccountTemplateCreated } from 'webapp/services/model-manager/boards';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TrackingOriginForTemplateCreated as TrackingOriginForReportAccountTemplateCreated } from 'webapp/services/model-manager/reports';

export type TrackingOriginForTemplatePreview = 'sidebar' | 'library';

export type TrackingOriginForTemplateUsed = 'sidebar';

export default class ModelManagerTemplatesService extends ModelManagerForStoreService<
	TemplateRecord,
	Template
> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;

	get accountTemplateRecords(): TemplateRecord[] {
		return this.savedRecords.filter((item) => !item.isPublic);
	}

	get publicTemplateRecords(): TemplateRecord[] {
		return this.savedRecords.filter((item) => item.isPublic);
	}

	@cached
	get publicTemplateModels(): Template[] {
		return [
			...this.boardPublicTemplateModels,
			...this.reportPublicTemplateModels,
			...this.boardLoopPublicTemplateModels,
		];
	}

	override get objectText(): string {
		// for batch action title
		return 'template';
	}

	// ------ Board Templates ------

	get boardAccountTemplateRecords(): TemplateRecord[] {
		return this.accountTemplateRecords.filter(
			(item) => item.objectType === TemplateObjectType.BOARD,
		);
	}

	private _boardAccountTemplateModels: Template[] = [];

	@cached
	get boardAccountTemplateModels(): Template[] {
		this.updateModelCache(this._boardAccountTemplateModels, this.boardAccountTemplateRecords);
		return this._boardAccountTemplateModels;
	}

	get boardPublicTemplateRecords(): TemplateRecord[] {
		return this.publicTemplateRecords.filter(
			(item) => item.objectType === TemplateObjectType.BOARD,
		);
	}

	private _boardPublicTemplateModels: Template[] = [];

	@cached
	get boardPublicTemplateModels(): Template[] {
		this.updateModelCache(this._boardPublicTemplateModels, this.boardPublicTemplateRecords);
		return this._boardPublicTemplateModels;
	}

	// ------ Board Loop Templates ------

	get boardLoopAccountTemplateRecords(): TemplateRecord[] {
		return this.accountTemplateRecords.filter(
			(item) => item.objectType === TemplateObjectType.BOARD_LOOP,
		);
	}

	private _boardLoopAccountTemplateModels: Template[] = [];

	@cached
	get boardLoopAccountTemplateModels(): Template[] {
		this.updateModelCache(
			this._boardLoopAccountTemplateModels,
			this.boardLoopAccountTemplateRecords,
		);
		return this._boardLoopAccountTemplateModels;
	}

	get boardLoopPublicTemplateRecords(): TemplateRecord[] {
		const records = this.publicTemplateRecords.filter(
			(item) => item.objectType === TemplateObjectType.BOARD_LOOP,
		);
		return records;
	}

	private _boardLoopPublicTemplateModels: Template[] = [];

	@cached
	get boardLoopPublicTemplateModels(): Template[] {
		this.updateModelCache(
			this._boardLoopPublicTemplateModels,
			this.boardLoopPublicTemplateRecords,
		);
		return this._boardLoopPublicTemplateModels;
	}

	// ------ Report Templates ------

	get reportAccountTemplateRecords(): TemplateRecord[] {
		return this.accountTemplateRecords.filter(
			(item) => item.objectType === TemplateObjectType.REPORT,
		);
	}

	private _reportAccountTemplateModels: Template[] = [];

	@cached
	get reportAccountTemplateModels(): Template[] {
		this.updateModelCache(this._reportAccountTemplateModels, this.reportAccountTemplateRecords);
		return this._reportAccountTemplateModels;
	}

	get reportPublicTemplateRecords(): TemplateRecord[] {
		return this.publicTemplateRecords.filter(
			(item) => item.objectType === TemplateObjectType.REPORT,
		);
	}

	private _reportPublicTemplateModels: Template[] = [];

	@cached
	get reportPublicTemplateModels(): Template[] {
		this.updateModelCache(this._reportPublicTemplateModels, this.reportPublicTemplateRecords);
		return this._reportPublicTemplateModels;
	}

	get popularBoardPublicTemplateModels(): Template[] {
		return sortByPopularity(this.boardPublicTemplateModels);
	}

	private getRecommendedTemplateModels(count: number, models: Template[]): Template[] {
		let preformattedModel: Template[] = [];
		let formattedModel: Template[] = [];
		const { sourcesSelection, cloudSourcesOpportunity } = this.accountService.metadata;
		const model = sortBy(models, 'usedCount').reverse();
		const firstConnectedSourceTypeKey =
			this.mmSourcesService.firstConnectedCloudSourceType?.key;

		// Find templates
		const findRelavantTemplatesBySourceTypes = (sourceTypes: string[]) => {
			return model.filter(
				(i) =>
					i.specificSourceTypesKeys.length &&
					!i.specificSourceTypesKeys.some((k) => !sourceTypes.includes(k)),
			);
		};

		if (firstConnectedSourceTypeKey) {
			preformattedModel = findRelavantTemplatesBySourceTypes([
				firstConnectedSourceTypeKey,
			]).slice(0, 6); // The first 6 templates are from the first connected cloud source type
		}

		if (sourcesSelection.length) {
			formattedModel = findRelavantTemplatesBySourceTypes(
				sourcesSelection.filter((item) => item !== firstConnectedSourceTypeKey),
			);
		} else if (cloudSourcesOpportunity.length) {
			formattedModel = findRelavantTemplatesBySourceTypes(
				cloudSourcesOpportunity.filter((item) => item !== firstConnectedSourceTypeKey),
			);
		} else {
			formattedModel = model;
		}

		formattedModel.forEach((model) => {
			if (!preformattedModel.find((m) => m === model)) {
				preformattedModel.push(model);
			}
		});

		if (preformattedModel.length < count) {
			// add from the leftovers (skip duplicates)
			model.slice(0, count).forEach((model) => {
				if (!preformattedModel.find((m) => m === model)) {
					preformattedModel.push(model);
				}
			});
		}

		return preformattedModel.slice(0, count);
	}

	getRecommendedBoardPublicTemplateModels(count: number): Template[] {
		return this.getRecommendedTemplateModels(count, this.boardPublicTemplateModels);
	}

	getRecommendedReportPublicTemplateModels(count: number): Template[] {
		return this.getRecommendedTemplateModels(count, this.reportPublicTemplateModels);
	}

	getRecommendedBoardLoopPublicTemplateModels(count: number): Template[] {
		return this.getRecommendedTemplateModels(count, this.boardLoopPublicTemplateModels);
	}

	getRecommendedAccountTemplateModels(count: number): Template[] {
		return [
			...this.getRecommendedTemplateModels(count, this.boardAccountTemplateModels),
			...this.getRecommendedTemplateModels(count, this.boardLoopAccountTemplateModels),
		];
	}

	openPreviewPopup(
		template: Template,
		onTemplateUse: () => void,
		trackingOrigin?: TrackingOriginForTemplatePreview,
		onPopupClose?: () => void,
	) {
		this.trackEvent(template, TrackingEvent.TEMPLATE_PREVIEW, {
			origin: trackingOrigin,
		});

		let popup;

		if (
			template.objectType === TemplateObjectType.BOARD ||
			template.objectType === TemplateObjectType.BOARD_LOOP
		) {
			popup = this.mmBoardsService.createAndOpenPreviewPopup({
				template,
				trackingOrigin: null,
				onTemplateUse,
			});
		} else {
			popup = this.mmReportsService.createAndOpenPreviewPopup({
				template,
				onTemplateUse,
			});
		}

		if (onPopupClose) {
			popup.on(PopupEvent.CLOSING, this, () => {
				onPopupClose();
			});
		}

		return popup;
	}

	// null as model is passed only when we creating new account board/report template (without predefined board/report, which we can later select in modal)
	openAccountTemplateEditPopup(
		objectType: TemplateObjectType,
		model: Board | Report | BoardLoop | null,
		trackingOrigin:
			| TrackingOriginForBoardAccountTemplateCreated
			| TrackingOriginForReportAccountTemplateCreated
			| TrackingOriginForBoardLoopAccountTemplateCreated
			| null,
	): void {
		if (!this.billingService.checkIfCan(BillingFeature.ACCOUNT_TEMPLATES, true)) {
			return;
		}

		this.popupService
			.createModal(TemplatesAccountPopupEditComponent, {
				popupClass: 'popup-type-account-template-edit',
				allowStacked: true, // used because of: common/popup-permissions, user/popup-invite, user/popup-forbidden-action
			})
			.open({
				model,
				objectType,
				trackingOrigin,
			});
	}

	openPublicTemplateEditPopup(model: Board | Report | BoardLoop): void {
		this.popupService
			.createModal(TemplatesPublicPopupEditComponent, {
				popupClass: 'popup-type-public-template-edit title-bigger',
				allowStacked: true, // used because of: common/popups/confirm
			})
			.open({
				model,
			});
	}

	openAccountTemplateDeletePopup(
		model: Template,
		onConfirm: (keepBoardTemplates: boolean) => void,
	): void {
		this.popupService
			.createModal(TemplatesAccountPopupDeleteConfirmComponent, {
				popupClass: 'popup-type-confirm title-bigger',
			})
			.open({
				onConfirm,
				objectType: model.objectType,
				title: `Are you sure you want to delete the template "${model.title}"?`,
				text: '<strong>This cannot be undone.</strong>',
				confirmAction: 'Delete',
				cancelAction: 'Cancel',
				confirmWord: 'DELETE',
			});
	}

	openResolvePopup(
		model: ResolutionCenterItem | Template,
		onAllResolved: (data?: TemplateResolve.NormalizedResolvePayload) => void,
		trackOrigin?: string,
		onPopupUnload?: () => void,
	) {
		const popup = this.popupService.createModal(TemplatesPopupResolveTemplateComponent, {
			popupClass: 'popup-type-resolve-template',
			allowStacked: true,
			closeOnOverlayClick: false,
		});

		popup.open({
			model,
			onAllResolved,
			trackOrigin,
		});

		if (onPopupUnload) {
			popup.on(PopupEvent.CLOSED, this, () => {
				onPopupUnload();
			});
		}

		return popup;
	}

	// ------ Requests ------

	private generateVariablePrefix(objectType: TemplateObjectType, isPublic: boolean): string {
		let _objectType;

		switch (objectType) {
			case TemplateObjectType.BOARD:
				_objectType = 'board';
				break;
			case TemplateObjectType.REPORT:
				_objectType = 'report';
				break;
			case TemplateObjectType.BOARD_LOOP:
				_objectType = 'boardloop';
				break;
			default:
				break;
		}

		const _templateType = isPublic ? 'public' : 'account';

		return _objectType + _templateType + 'templates';
	}

	private requestTemplates(objectType: TemplateObjectType, isPublic: boolean): Promise<void> {
		const variablePrefix = this.generateVariablePrefix(objectType, isPublic);

		const queryParams = {
			type: objectType,
			isPublic,
		};

		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				/* if (objectType === TemplateObjectType.REPORT && isPublic) {
				const reply = window.dbx_mock['GET /d/templates'];
				return new Promise((resolve, reject) => {
					resolve(reply);
				});
			} */

				return this.requestService.api<RecordData.Payload>(
					'GET',
					'/templates',
					queryParams,
				);
			},
		);
	}

	requestBoardAccountTemplates(): Promise<void> {
		return this.requestTemplates(TemplateObjectType.BOARD, false);
	}

	requestBoardPublicTemplates(): Promise<void> {
		return this.requestTemplates(TemplateObjectType.BOARD, true);
	}

	requestReportAccountTemplates(): Promise<void> {
		return this.requestTemplates(TemplateObjectType.REPORT, false);
	}

	requestReportPublicTemplates(): Promise<void> {
		return this.requestTemplates(TemplateObjectType.REPORT, true);
	}

	requestBoardLoopAccountTemplates(): Promise<void> {
		return this.requestTemplates(TemplateObjectType.BOARD_LOOP, false);
	}

	requestBoardLoopPublicTemplates(): Promise<void> {
		return this.requestTemplates(TemplateObjectType.BOARD_LOOP, true);
	}

	useTemplate(
		model: Template,
		payload: TemplateResolve.NormalizedResolvePayload,
		trackingOrigin?: TrackingOriginForTemplateUsed,
	): Promise<APIData.Templates.UseTemplateResponse> {
		return this.requestService
			.apiWithAsyncReply<APIData.Templates.UseTemplateResponse>(
				'POST',
				`/templates/${model.id}`,
				payload,
			)
			.then((reply) => {
				this.triggerTemplateUsedEvent(model, trackingOrigin);

				return reply;
			});
	}

	// "Use template" on an account template: create, then the services' own follow-up that shows the new board, loop or
	// report in its list.
	createFromAccountTemplate(template: Template): void {
		if (template.objectType === TemplateObjectType.BOARD) {
			this.mmBoardsService.createBoardFromTemplate(template, 'account_template', (record) => {
				this.mmBoardsService.afterCreateBoardFromTemplate(record);
			});
			return;
		}

		if (template.objectType === TemplateObjectType.BOARD_LOOP) {
			this.mmBoardLoopsService.createBoardLoopFromTemplate(
				template,
				'account_template_loop',
				(record) => {
					this.mmBoardLoopsService.afterCreateBoardLoopFromTemplate(record);
				},
			);
			return;
		}

		this.mmReportsService.createReportFromTemplate(
			template,
			'report_account_template',
			this.mmReportsService.afterCreateReportFromTemplate,
		);
	}

	fetchBoardsFromTemplate(
		templateBoardId: number,
	): Promise<APIData.Templates.GetBoardsFromTemplateResponse> {
		return this.requestService
			.api<APIData.Templates.GetBoardsFromTemplateResponse>(
				'GET',
				`/templates/${templateBoardId}/usages`,
			)
			.then((reply) => {
				// reply = [{"id":12345,"spaceId":2,"sourceTypes":[15,39],"image":"https://dashboardsnapshotsstage.s3.amazonaws.com/c4lq627tbn2.jpg","name":"No. 1","created":1701963863,"createdByName":"Miha","modifiedByName":"Someone else","modified":1701963882,"allSourceTypesAvailable":true},{"id":23456,"spaceId":5,"sourceTypes":[39],"image":"https://dashboardsnapshotsstage.s3.amazonaws.com/2etepmj6iogk.jpg","name":"No. 2","created":1701964997,"createdByName":"Miha","modifiedByName":"Mihael Test","modified":1701663894,"allSourceTypesAvailable":false}];
				return reply;
			});
	}

	requestPublicBoardLoopData = (templateToken: string): Promise<RecordData.Payload> => {
		// requesting on the "public" URL since this board loop might not be in the current space
		const url = `/token/${templateToken}/board-loop`;

		return this.requestService.api<RecordData.Payload>('GET', url);
	};

	updateBoardsFromTemplate(templateBoardId: number, boardIds: number[]) {
		let doneBoardsCount = 0;
		if (templateBoardId) {
			const layer = this.infolayerService.loading('Updating dashboards from template...');
			return this.requestService
				.apiWithAsyncReply(
					'POST',
					`/templates/${templateBoardId}/apply-changes`,
					boardIds,
					undefined,
					() => {
						doneBoardsCount++;
						layer.text = `Updating dashboard from template... (${doneBoardsCount}/${boardIds.length})`;
						return doneBoardsCount === boardIds.length;
					},
				)
				.then(() => {
					layer.ok('All dashboards updated!');
				})
				.catch((errorData: RequestError) => {
					layer.close();
					throw errorData;
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
		return Promise.resolve();
	}

	// ------ Tracking ------

	trackEvent(
		template: TemplateRecord | Template,
		eventName: TrackingEvent,
		changedProps: APIData.Tracking.Data = {},
	) {
		const eventProps = {
			title: template.title,
			sources: (template.sources || []).map((sourceType: number) => {
				return this.mmSourceTypesService.findModelById(sourceType)?.key;
			}),
			type: template.isPublic ? 'public' : 'account',
			object_type: this.getTemplateObjectTypeString(template.objectType),
		};

		this.analyticsService.event(eventName, { ...eventProps, ...changedProps });
	}

	getTemplateObjectTypeString(objectType: TemplateObjectType): string {
		switch (objectType) {
			case TemplateObjectType.BOARD:
				return 'board';
			case TemplateObjectType.REPORT:
				return 'report';
			case TemplateObjectType.BOARD_LOOP:
				return 'loop';
			default:
				return '';
		}
	}

	trackBoardLoopTemplateEvent(
		template: TemplateRecord | Template,
		origin: string,
		event: TrackingEvent,
	) {
		// track first event (save board loop as template)
		this.trackEvent(template, event, { origin });

		const boardIds = template.boardIds;

		// get boards
		const boards = boardIds.map((boardId) => this.mmBoardsService.findModel(String(boardId)));

		// track all created board templates
		boards.forEach((board) => {
			const fromLoop =
				event === TrackingEvent.TEMPLATE_CREATED
					? {
							from_loop:
								board?.templateModel?.objectType === TemplateObjectType.BOARD_LOOP,
						}
					: {};

			if (board?.templateModel) {
				this.trackEvent(board.templateModel, event, {
					origin,
					...fromLoop,
				});
			}
		});
	}

	private triggerTemplateUsedEvent(
		model: Template,
		trackingOrigin?: TrackingOriginForTemplateUsed,
	) {
		const metrics = model.metricKeys.map((metricKey: TemplateMetricKey) => metricKey.name);

		this.trackEvent(model, TrackingEvent.TEMPLATE_USED, { metrics, origin: trackingOrigin });
	}

	// ------ For-store ------

	modelName = ModelName.TEMPLATE;
	override modelTitle = 'dashboard template';

	createModel(model: TemplateRecord): Template {
		return new Template({ model });
	}

	override createNewModel(properties: CreateRecordProperties<TemplateRecord> = {}): Template {
		if (properties.isPublic) {
			properties.createdBy = {
				name: 'Databox',
				url: null,
				avatar: 'https://cdn1.databox.com/favicons/databox_avatar_512.png',
			};
		}

		return super.createNewModel(properties);
	}

	createNewBoardAccountTemplateModel(properties: CreateRecordProperties<TemplateRecord> = {}) {
		properties.objectType = TemplateObjectType.BOARD;
		properties.isPublic = false;

		return this.createNewModel(properties);
	}

	createNewBoardPublicTemplateModel(properties: CreateRecordProperties<TemplateRecord> = {}) {
		properties.objectType = TemplateObjectType.BOARD;
		properties.isPublic = true;

		return this.createNewModel(properties);
	}

	createNewReportAccountTemplateModel(properties: CreateRecordProperties<TemplateRecord> = {}) {
		properties.objectType = TemplateObjectType.REPORT;
		properties.isPublic = false;

		return this.createNewModel(properties);
	}

	createNewReportPublicTemplateModel(properties: CreateRecordProperties<TemplateRecord> = {}) {
		properties.objectType = TemplateObjectType.REPORT;
		properties.isPublic = true;

		return this.createNewModel(properties);
	}

	createNewBoardLoopAccountTemplateModel(
		properties: CreateRecordProperties<TemplateRecord> = {},
	) {
		properties.objectType = TemplateObjectType.BOARD_LOOP;
		properties.isPublic = false;

		return this.createNewModel(properties);
	}

	createNewBoardLoopPublicTemplateModel(properties: CreateRecordProperties<TemplateRecord> = {}) {
		properties.objectType = TemplateObjectType.BOARD_LOOP;
		properties.isPublic = true;

		return this.createNewModel(properties);
	}

	deleteTemplate(model: Template, skipConfirmPopup = false): Promise<void> {
		if (this.accountService.handleDemo()) {
			return Promise.reject();
		}

		return new Promise((resolve) => {
			const confirm = (keepBoardTemplates: boolean) => {
				const deleteTemplatePromise = (): Promise<unknown> => {
					if (model.objectType === TemplateObjectType.BOARD) {
						return model.delete();
					} else {
						this.mmTemplatesService.trackBoardLoopTemplateEvent(
							model,
							'',
							TrackingEvent.TEMPLATE_DELETED,
						);

						void model.delete(true);

						return this.requestService.api(
							'DELETE',
							`/templates/${model.id}?keepBoardTemplates=${keepBoardTemplates}`,
						);
					}
				};

				const infolayer = this.infolayerService.loading('Deleting template...');

				deleteTemplatePromise()
					.then(
						() => {
							infolayer.ok(`Template <strong>${model.title}</strong> deleted!`);

							resolve();
						},
						(errorData) => {
							infolayer.close();
							throw errorData;
						},
					)
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
			};

			if (skipConfirmPopup) {
				confirm(true);
			} else {
				this.openAccountTemplateDeletePopup(model, confirm);
			}
		});
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/templates': ModelManagerTemplatesService;
	}
}
